import { Observable } from 'rxjs';
import { $httpClient } from '@/services/httpClient';
import { OfficesModel } from '@/models/Offices.model';
import { UnitsModel } from '@/models/Units.model';
import {
  APPROVE_ALL_FORMS,
  APPROVE_FORM,
  APPROVE_SELECTED_FORMS,
  CREATE_MASKIT_UPDATE_FILE,
  CREATE_ADP_UPDATE_FILE,
  UNDO_MARK_AS_UPDATE,
  DELETE_FORM,
  DELETE_MANUAL_FORM,
  FORMS_SEARCH,
  GENERATE_COMPARE_RESULT_SEARCH_REPORT,
  GENERATE_CUBE_DETAILS_REPORT,
  GENERATE_DETAILED_FORMS_REPORT,
  GENERATE_EMPTY_FORM_101_AS_PDF,
  GENERATE_FORM_CHANGES_REPORT,
  GENERATE_FORMS_SEARCH_REPORT,
  GENERATE_GROUP_TYPE_EXPORT_EVENTS_REPORT,
  GENERATE_MANAGERS_REPORT,
  GET_CUBE_DETAILS,
  GET_CUBES_INFO,
  GET_CUBES_STATS,
  GET_EMPLOYEE_EMAIL,
  GET_FORM101_OFFICES_LIST,
  GET_FORM101_UNITS_LIST,
  GET_FORM_101_CHANGES,
  GET_FORM_101_FILTERS_LIST,
  GET_FORM_101_REJECT_REASON,
  GET_FORM_101_STATUSES_LIST,
  GET_FORM_DATA,
  GET_FORM_STATUS_HISTORY_LIST,
  GET_ID_NUMBER_FIELDS,
  GET_MANAGER_DOCUMENT_CONTENT,
  GET_MANAGER_DOCUMENT_LIST,
  GET_MANUAL_FORM_CONTENT,
  GET_STATISTICS,
  GET_STATISTICS_OFFICES,
  MANAGER_GENERATE_FORM_101_AS_PDF,
  MANAGER_GET_FORM_101_AS_PDF,
  MANAGER_GET_MANUAL_FORM_101_AS_PDF,
  MANUAL_UPDATE_IPT,
  MARK_AS_IGNORE,
  REJECT_FORM,
  RESET_PASSWORD,
  SAVE_FORM_101_CHANGES_NOTES,
  UNDO_MARK_AS_IGNORE,
  UPDATE_IPT,
  UPLOAD_MANUAL_FORM,
} from '@/types/managerForm.types';
import { Form101SearchModel } from '@/models/Form101Search.model';
import { Form101StatisticsModel } from '@/models/manager/Form101Statistics.model';
import { Form101DataModel } from '@/models/form/Form101Data.model';
import { ManagerResetPasswordRequest } from '@/models/manager/ManagerResetPasswordRequest';
import { BaseResponse } from '@/models/response/base/BaseResponse';
import { GetDocumentResponse } from '@/models/manager/GetDocumentResponse';
import { ManagerForm101Data } from '@/models/manager/ManagerForm101Data.model';
import { FormsSearchRequest } from '@/models/manager/ManagerFormsSearchRequest';
import { GetEmployeeEmailResponse } from '@/models/manager/GetEmployeeEmailResponse';
import { ApproveSelectedFormsRequest } from '@/models/manager/ApproveSelectedFormsRequest';
import { GridResponse } from '@/models/response/base/GridResponse';
import { CubesSelectedEmpsRequestModel } from '@/models/manager/CubesSelectedEmpsRequest.model';
import { CompareResultSearchRequestModel } from '@/models/manager/CompareResultSearchRequest.model';
import { InputFile } from '@/models/InputFile.model';
import { DocumentRequestModel } from '@/models/manager/DocumentRequest.model';

export default class ManagerService {
  private readonly baseApi: string;
  private readonly formsBaseApi: string;
  private readonly statisticsBaseApi: string;
  private readonly cubesBaseApi: string;
  private readonly managerReportApi: string;

  constructor() {
    this.baseApi = 'api/mng/';
    this.formsBaseApi = this.baseApi + 'forms/';
    this.statisticsBaseApi = this.baseApi + 'statistics/';
    this.cubesBaseApi = this.baseApi + 'cubes/';
    this.managerReportApi = 'api/bo/manager/';
  }

  public getDocumentContent(documentRequest: DocumentRequestModel): Observable<any> {
    return $httpClient.get<any>(this.formsBaseApi + GET_MANAGER_DOCUMENT_CONTENT +
      '?docId=' + documentRequest.docId + '&officeNum=' + documentRequest.officeNum +
      '&idNum=' + documentRequest.idNum + '&positionNum=' + documentRequest.positionNum);
  }

  public getFormDocumentsList(documentRequest: DocumentRequestModel): Observable<any> {
    return $httpClient.get<any>(this.formsBaseApi + GET_MANAGER_DOCUMENT_LIST +
      '?formId=' + documentRequest.formId + '&officeNum=' + documentRequest.officeNum +
      '&idNum=' + documentRequest.idNum + '&positionNum=' + documentRequest.positionNum);
  }

  public getForm101OfficesList(): Observable<OfficesModel> {
    return $httpClient.get<any>(this.formsBaseApi + GET_FORM101_OFFICES_LIST);
  }

  public getForm101UnitsList(officeNum: number): Observable<UnitsModel> {
    return $httpClient.get<any>(this.formsBaseApi + GET_FORM101_UNITS_LIST + '?officeNum=' + officeNum);
  }

  public getForm101FiltersList(): Observable<any> {
    return $httpClient.get<any>(this.formsBaseApi + GET_FORM_101_FILTERS_LIST);
  }

  public getIdNumberFields(): Observable<any> {
    return $httpClient.get<any>(this.formsBaseApi + GET_ID_NUMBER_FIELDS);
  }

  public formsSearch(formsSearchObject: Form101SearchModel): Observable<Form101SearchModel> {
    return $httpClient.post<any>(this.formsBaseApi + FORMS_SEARCH, formsSearchObject, {
      timeout: 120000
    });
  }

  public getStatistics(): Observable<Form101StatisticsModel> {
    return $httpClient.get<any>(this.statisticsBaseApi + GET_STATISTICS);
  }

  public getStatisticsOffices(): Observable<Form101StatisticsModel> {
    return $httpClient.get<any>(this.statisticsBaseApi + GET_STATISTICS_OFFICES);
  }

  public approveForm(formData: Form101DataModel): Observable<any> {
    return $httpClient.post(this.formsBaseApi + APPROVE_FORM, formData);
  }

  public rejectForm(formData: Form101DataModel): Observable<any> {
    return $httpClient.post(this.formsBaseApi + REJECT_FORM, formData);
  }

  public saveForm101ChangesNotes(formData: Form101DataModel): Observable<any> {
    return $httpClient.post(this.formsBaseApi + SAVE_FORM_101_CHANGES_NOTES, formData);
  }

  public getFormStatusHistoryList(formData: Form101DataModel): Observable<any> {
    return $httpClient.get(this.formsBaseApi + GET_FORM_STATUS_HISTORY_LIST +
      '?formId=' + formData.formId + '&officeNum=' + formData.officeNum +
      '&idNum=' + formData.idNum + '&positionNum=' + formData.positionNum);
  }

  public getForm101Changes(formData: Form101DataModel): Observable<any> {
    return $httpClient.get(this.formsBaseApi + GET_FORM_101_CHANGES +
      '?formId=' + formData.formId + '&officeNum=' + formData.officeNum +
      '&idNum=' + formData.idNum + '&positionNum=' + formData.positionNum);
  }

  // public getCubesForm101Changes(formData: Form101DataModel): Observable<any> {
  //   return $httpClient.get(this.cubesBaseApi + GET_FORM_101_CHANGES +
  //     '?formId=' + formData.formId + '&officeNum=' + formData.officeNum +
  //     '&idNum=' + formData.idNum + '&positionNum=' + formData.positionNum);
  // }

  public getForm101RejectReason(formData: Form101DataModel): Observable<any> {
    return $httpClient.get(this.formsBaseApi + GET_FORM_101_REJECT_REASON +
      '?formId=' + formData.formId + '&officeNum=' + formData.officeNum +
      '&idNum=' + formData.idNum + '&positionNum=' + formData.positionNum);
  }

  public getFormData(formData: Form101DataModel): Observable<any> {
    return $httpClient.get(this.formsBaseApi + GET_FORM_DATA +
      '?formId=' + formData.formId + '&officeNum=' + formData.officeNum +
      '&idNum=' + formData.idNum + '&positionNum=' + formData.positionNum);
  }

  // public getCubesFormData(formData: Form101DataModel): Observable<any> {
  //   return $httpClient.get(this.cubesBaseApi + GET_FORM_DATA +
  //     '?formId=' + formData.formId + '&officeNum=' + formData.officeNum +
  //     '&idNum=' + formData.idNum + '&positionNum=' + formData.positionNum);
  // }

  public getForm101AsPdf(managerForm101Data: ManagerForm101Data): Observable<any> {
    return $httpClient.get(this.formsBaseApi + MANAGER_GET_FORM_101_AS_PDF +
      '?docId=' + managerForm101Data.docId + '&officeNum=' + managerForm101Data.officeNum +
      '&idNum=' + managerForm101Data.idNum + '&positionNum=' + managerForm101Data.positionNum);
  }

  public generateForm101AsPdf(managerForm101Data: ManagerForm101Data): Observable<any> {
    return $httpClient.get(this.formsBaseApi + MANAGER_GENERATE_FORM_101_AS_PDF +
      '?formId=' + managerForm101Data.formId + '&officeNum=' + managerForm101Data.officeNum +
      '&idNum=' + managerForm101Data.idNum + '&positionNum=' + managerForm101Data.positionNum);
  }

  public managerGetManualForm101AsPdf(managerForm101Data: ManagerForm101Data): Observable<any> {
    return $httpClient.get(this.formsBaseApi + MANAGER_GET_MANUAL_FORM_101_AS_PDF +
      '?docId=' + managerForm101Data.docId + '&officeNum=' + managerForm101Data.officeNum +
      '&idNum=' + managerForm101Data.idNum + '&positionNum=' + managerForm101Data.positionNum);
  }

  public generateEmptyForm101AsPdf(managerForm101Data: ManagerForm101Data): Observable<any> {
    return $httpClient.get(this.formsBaseApi + GENERATE_EMPTY_FORM_101_AS_PDF +
      '?officeNum=' + managerForm101Data.officeNum + '&idNum=' + managerForm101Data.idNum
      + '&positionNum=' + managerForm101Data.positionNum + '&taxYear=' + managerForm101Data.taxYear);
  }

  public getForm101StatusesList(): Observable<any> {
    return $httpClient.get(this.formsBaseApi + GET_FORM_101_STATUSES_LIST);
  }

  public resetPassword(managerResetPasswordRequest: ManagerResetPasswordRequest): Observable<BaseResponse> {
    return $httpClient.post<BaseResponse>(this.formsBaseApi + RESET_PASSWORD, managerResetPasswordRequest);
  }

  public deleteForm(managerForm101Data: ManagerForm101Data): Observable<BaseResponse> {
    return $httpClient.delete<BaseResponse>(this.formsBaseApi + DELETE_FORM +
      '?formId=' + managerForm101Data.formId + '&officeNum=' + managerForm101Data.officeNum +
      '&idNum=' + managerForm101Data.idNum + '&positionNum=' + managerForm101Data.positionNum);
  }

  public deleteManualForm(managerForm101Data: ManagerForm101Data): Observable<BaseResponse> {
    return $httpClient.delete<BaseResponse>(this.formsBaseApi + DELETE_MANUAL_FORM +
    '?officeNum=' + managerForm101Data.officeNum + '&idNum=' + managerForm101Data.idNum +
    '&positionNum=' + managerForm101Data.positionNum + '&dateFrom=' + managerForm101Data.dateFrom +
     '&dateTo=' + managerForm101Data.dateTo);
  }

  public getManualFormContent(managerForm101Data: ManagerForm101Data): Observable<GetDocumentResponse> {
    return $httpClient.get(this.formsBaseApi + GET_MANUAL_FORM_CONTENT +
      '?docId=' + managerForm101Data.docId + '&officeNum=' + managerForm101Data.officeNum +
      '&idNum=' + managerForm101Data.idNum + '&positionNum=' + managerForm101Data.positionNum);
  }

  public generateFormsSearchReport(formsSearchRequest: FormsSearchRequest): Observable<any> {
    return $httpClient.post(this.formsBaseApi + GENERATE_FORMS_SEARCH_REPORT, formsSearchRequest, {
      timeout: 180000,
    });
  }

  public generateDetailedFormsReport(formsSearchRequest: FormsSearchRequest): Observable<any> {
    return $httpClient.post(this.formsBaseApi + GENERATE_DETAILED_FORMS_REPORT, formsSearchRequest, {
      timeout: 90000
    });
  }

  public generateFormChangesReport(managerForm101Data: ManagerForm101Data): Observable<any> {
    return $httpClient.get<BaseResponse>(this.formsBaseApi + GENERATE_FORM_CHANGES_REPORT +
      '?formId=' + managerForm101Data.formId + '&officeNum=' + managerForm101Data.officeNum +
      '&idNum=' + managerForm101Data.idNum + '&positionNum=' + managerForm101Data.positionNum);
  }

  public getEmployeeEmail(managerForm101Data: ManagerForm101Data): Observable<GetEmployeeEmailResponse> {
    return $httpClient.get(this.formsBaseApi + GET_EMPLOYEE_EMAIL +
      '?officeNum=' + managerForm101Data.officeNum + '&idNum=' + managerForm101Data.idNum +
      '&positionNum=' + managerForm101Data.positionNum);
  }

  public approveAllForms(form101SearchModel: Form101SearchModel): Observable<BaseResponse> {
    return $httpClient.post<BaseResponse>(this.formsBaseApi + APPROVE_ALL_FORMS, form101SearchModel, {
      timeout: 600000
    });
  }

  public approveSelectedForms(approveSelectedFormsRequest: ApproveSelectedFormsRequest): Observable<BaseResponse> {
    return $httpClient.post(this.formsBaseApi + APPROVE_SELECTED_FORMS, approveSelectedFormsRequest, {
      timeout: 300000
    });
  }

  public getCubesInfo(): Observable<GridResponse> {
    return $httpClient.get(this.cubesBaseApi + GET_CUBES_INFO);
  }

  public getCubeDetails(formsSearchRequest: FormsSearchRequest): Observable<GridResponse> {
    return $httpClient.post(this.cubesBaseApi + GET_CUBE_DETAILS, formsSearchRequest);
  }

  public getCubesStats(): Observable<GridResponse> {
    return $httpClient.get(this.cubesBaseApi + GET_CUBES_STATS, { timeout: 300000 });
  }

  public updateIpt(cubesSelectedEmpsRequest: CubesSelectedEmpsRequestModel): Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + UPDATE_IPT, cubesSelectedEmpsRequest);
  }

  public markAsIgnore(cubesSelectedEmpsRequest: CubesSelectedEmpsRequestModel): Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + MARK_AS_IGNORE, cubesSelectedEmpsRequest);
  }

  public undoMarkAsIgnore(cubesSelectedEmpsRequest: CubesSelectedEmpsRequestModel): Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + UNDO_MARK_AS_IGNORE, cubesSelectedEmpsRequest);
  }

  public createMaskitUpdateFile(cubesSelectedEmpsRequest: CubesSelectedEmpsRequestModel): Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + CREATE_MASKIT_UPDATE_FILE, cubesSelectedEmpsRequest, {
      timeout: 300000
    });
  }

  public createAdpUpdateFile(cubesSelectedEmpsRequest: CubesSelectedEmpsRequestModel): Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + CREATE_ADP_UPDATE_FILE, cubesSelectedEmpsRequest, {
      timeout: 300000
    });
  }

  public undoMarkAsUpdate(cubesSelectedEmpsRequest: CubesSelectedEmpsRequestModel): Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + UNDO_MARK_AS_UPDATE, cubesSelectedEmpsRequest);
  }

  public generateCubeDetailsReport(compareResultSearchRequest: CompareResultSearchRequestModel):
    Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + GENERATE_CUBE_DETAILS_REPORT, compareResultSearchRequest);
  }

  public exportManagersByCustomer(indexNum: number):
    Observable<BaseResponse> {
    return $httpClient.get(this.managerReportApi + GENERATE_MANAGERS_REPORT + '?indexNum=' + indexNum);
  }

  public generateCompareResultSearchReport(compareResultSearchRequest: CompareResultSearchRequestModel):
    Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + GENERATE_COMPARE_RESULT_SEARCH_REPORT, compareResultSearchRequest);
  }

  public generateGroupTypeExportEventsReport(compareResultSearchRequest: CompareResultSearchRequestModel):
    Observable<BaseResponse> {
    return $httpClient.post(this.cubesBaseApi + GENERATE_GROUP_TYPE_EXPORT_EVENTS_REPORT, compareResultSearchRequest);
  }

  public uploadManualForm(
    file: InputFile, docType: string, officeNum: string, idNum: string, positionNum: string, taxYear: string,
  ):
    Observable<BaseResponse> {
    const formData = new FormData();
    formData.append('file', file.file);
    formData.append('fileName', file.name);
    formData.append('officeNum', officeNum);
    formData.append('idNum', idNum);
    formData.append('positionNum', positionNum);
    formData.append('taxYear', taxYear);
    return $httpClient.post(this.formsBaseApi + UPLOAD_MANUAL_FORM, formData);
  }

  public manualUpdateIpt(manualUpdateIptModel: Form101DataModel): Observable<BaseResponse> {
    return $httpClient.post(this.formsBaseApi + MANUAL_UPDATE_IPT, manualUpdateIptModel);
  }

}
